

import React, {
    Fragment, useContext, useImperativeHandle, forwardRef,
    useState, useEffect
} from "react";
import { useTranslation } from "react-i18next";
import { FiZoomIn } from "react-icons/fi";
import { highlightAndZoomToFeatureUsingLayerView, getValueCaseInsensitive, removeAllHighlights } from "../../../../utils/Helpers";
import Graphic from "@arcgis/core/Graphic";
import geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { SearchContext } from "./Search";
import { DataExport } from "../DataExport/DataExport";
import { clipFeatures } from "./SearchUtils";
import { FaPlus, FaMinus } from "react-icons/fa6";
import { getLocalStorageItem } from "../../../../utils/HelperFunctions";
import { useUserGroups } from "../../../../components/common/Auth/UserGroupsContext";
import { useMapServices } from "../../../../components/MapServiceContext";
import Select from "react-select";
import CustomDropdownIndicator from "../../../../components/common/CustomDropdownIndicator";

const SearchResult = forwardRef((props, ref) => {
    const { items, clipgeometry, mapSearchRef } = props;
    const { t } = useTranslation();
    const { mapview, showLoading, toggleResultActions,
        selectedRFeatures: selectedFeatures, setSelectedRFeatures: setSelectedFeatures,
        selectAllChecked, setSelectAllChecked, activeSearch,
        selectedResultLayerOption: selectedOption,
        setSelectedResultLayerOption: setSelectedOption
    } = useContext(SearchContext);
    const [uniqueLayerNames, setUniqueLayerNames] = useState(null);
    const [resultData, setResultData] = useState(null);
    const [selectedItems, setSelectedItems] = useState({});
    const [selectedIndices, setSelectedIndices] = useState([]);
    const [showDetailsFor, setShowDetailsFor] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [searchResultsCount, setSearchResultsCount] = useState(0);
    const { groupFunctions } = useUserGroups();
    const { mapServiceInfo } = useMapServices();
    const [viewonlyFeatures, setViewonlyFeatures] = useState(false);

    const handleViewOnlyFeatures = (event) => {
        setViewonlyFeatures(event.target.checked);
        const visibleLayers = [];
        mapview.map.layers.forEach((layer) => {
            if (layer.visible) {
                if (layer.allSublayers) {
                    layer.allSublayers.forEach((sublayer) => {
                        if (sublayer.visible) {
                            visibleLayers.push(sublayer.title);
                        }
                    });
                }
            }
        })

        mapview.map.layers.forEach((layer) => {
            if (layer.id === 'clipedGraphicsLayer' || layer.id === 'searchDrawGLayer') {
                if (layer.id === 'searchDrawGLayer') {
                    layer.visible = true;
                }
                else {

                    layer.visible = event.target.checked;
                    layer.graphics.forEach((graphic) => {
                        if (activeSearch === 'map') {
                            if (visibleLayers.includes(graphic.layerName)) {
                                graphic.visible = event.target.checked;
                            }
                            else {
                                graphic.visible = false;
                            }
                        }
                        else {
                            graphic.visible = event.target.checked;
                        }
                        if (graphic.geometry?.type === 'point' && graphic.symbol?.type === 'picture-marker') {
                            // If original width/height exist, use them; else fallback
                            const originalWidth = graphic.symbol.width || 24;
                            const originalHeight = graphic.symbol.height || 24;
                            graphic.symbol.width = originalWidth;
                            graphic.symbol.height = originalHeight;
                        }
                    })
                }
            }
            else {
                // Hide all other layers
                layer.visible = !event.target.checked;
            }
        });
        const gLayer = mapview.map.findLayerById('searchDrawGLayer');
        if (gLayer) {
            mapview.map.reorder(gLayer, mapview.map.layers.length - 1);
        }
    };

    useEffect(() => {
        setIsLoggedIn(!!getLocalStorageItem('token'));
    }, []);
    useEffect(() => {
        setSelectedFeatures([]);
        setSelectedIndices([]);
},[resultData])

    const handleIResultLayerChange = (event) => {
        showLoading(true);
        // setSelectedOption(event.target.value);
        setSelectedOption(event.value);
        setSelectedFeatures([]);
        setSelectedIndices([]);
        setSelectAllChecked(false);
    };

    const updateSelectedItems = (layerName, values) => {
        setSelectedItems((prevLayers) => {
            const existingValues = prevLayers[layerName] || [];
            return {
                ...prevLayers,
                [layerName]: [...existingValues, ...values],
            };
        });
    };

    useEffect(() => {
        async function fetchData() {
            if (activeSearch === 'map') {
                if (!items || items.length === 0) {
                    showLoading(false);
                    setResultData([]);
                    return;
                }
                const uniqueLNames = [...new Set(items.flatMap(item => Object.keys(item)))];
                setUniqueLayerNames(uniqueLNames);
                if (uniqueLNames.length > 0) {
                    setSelectedOption(uniqueLNames[0]);
                }
                // console.log('query-result: items ', items);
            } else {
                showLoading(false);
                setResultData(items[0]);
                setSearchResultsCount(items[0]?.features.length)
            }
            const cliplayer = mapview.map.findLayerById('clipedGraphicsLayer');
            if (cliplayer) {
                cliplayer.visible = false;
                showLoading(true);
                await clipFeatures(cliplayer, items, mapview, clipgeometry, mapSearchRef, activeSearch);
                showLoading(false);
            }
        }
        fetchData();
    }, [items]);

    useEffect(() => {
        if (selectedOption && activeSearch === 'map') {
            const data = items.flatMap((res) => res[selectedOption] || []);
            // console.log('query-result: data ', data);
            setResultData(data[0]);
            setSearchResultsCount(data[0]?.features.length)
            showLoading(false);
        }
    }, [selectedOption, items]);

    const handleIResultItemClick = (item, featureIndex) => {
        const featureID = getValueCaseInsensitive(item.attributes, 'objectid');
        setSelectedFeatures(prevSelectedFeatures => {
            const prevSelectedFeatureIDs = prevSelectedFeatures
                .filter(Boolean)
                .map((ft) => getValueCaseInsensitive(ft.attributes, 'objectid'));

            if (prevSelectedFeatureIDs.includes(featureID)) {
                return prevSelectedFeatures.filter(f => f !== item);
            } else {
                return [...prevSelectedFeatures, item];
            }
        });

        setSelectedIndices(prevSelectedIndices => {
            if (prevSelectedIndices.includes(featureIndex)) {
                return prevSelectedIndices.filter(i => i !== featureIndex);
            } else {
                return [...prevSelectedIndices, featureIndex];
            }
        });
    };

    useEffect(() => {
        if (selectedFeatures && selectedFeatures.length > 0) {
            toggleResultActions(true);
        } else {
            toggleResultActions(false);
        }
    }, [selectedFeatures]);

    const zoomToSelected = () => {
        if (!selectedFeatures || selectedFeatures.length === 0) {
            return;
        }

        highlightAndZoomToFeatureUsingLayerView(selectedFeatures, mapview, null);
    };

    const clearSearchResultSelection = () => {
        setSelectedFeatures([]);
        setSelectedIndices([]);
        removeAllHighlights();
    };

    useImperativeHandle(ref, () => ({
        clearSearchResultSelection,
        setSelectedFeatures
    }));

    const toggleSelectAll = (event) => {
        const selectAll = event.target.checked;
        showLoading(true);
        if (!selectAll) {
            setSelectedFeatures([]);
            setSelectedIndices([]);
        } else {
            const allFeatures = resultData.features;
            const allFeatureIDs = allFeatures.map(result =>
                getValueCaseInsensitive(result.attributes, 'objectid')
            );
            const allFeatureIndices = allFeatures.map((_, index) => index);

            setSelectedFeatures(allFeatures);
            setSelectedIndices(allFeatureIndices);
        }
        setSelectAllChecked(!selectAllChecked);
        showLoading(false);
    };

    const toggleItemDetails = (evt, featureID) => {
        evt.stopPropagation();
        setShowDetailsFor(prevFeatureID => (prevFeatureID === featureID ? undefined : featureID));
    };

    // Filter attributes based on showInSearch
    const getFilteredAttributes = (layerId, attributes) => {
        //const layer = mapServiceInfo ? mapServiceInfo.find(service => service.layers.some(l => l.layerName.toLowerCase() === layerId.toLowerCase())) : '';
        const layer = mapServiceInfo
            ? Object.values(mapServiceInfo).find(service => service.layers.some(l => l.layerName.toLowerCase() === layerId.toLowerCase()))
            : null;

        //const layer = mapServiceInfo.find(service => service.layers.some(l => l.layerName.toLowerCase() === layerId.toLowerCase()));
        //const layer = mapServiceInfo ? mapServiceInfo.find(service => service.layers.some(l => l.layerName.toLowerCase() === layerId.toLowerCase())) : "";
        if (!layer) return attributes;

        const relevantLayer = layer.layers.find(l => l.layerName.toLowerCase() === layerId.toLowerCase());
        if (!relevantLayer) return attributes;

        const visibleFields = relevantLayer.fields.filter(field => field.showInSearch === true);
        const filteredAttributes = Object.entries(attributes)
            .filter(([key]) => visibleFields.some(field => field.fieldName.toLowerCase() === key.toLowerCase()));

        return Object.fromEntries(filteredAttributes);
    };

    const layerNames = uniqueLayerNames?.map((lname, resultIndex) => ({
        key: `option-${lname}-${resultIndex}`,
        value: lname,
        label: lname
    }));

    return (
        <div className="items-wrapper d-flex flex-column">
            {isLoggedIn && resultData && resultData.features && groupFunctions?.includes(window.userFunctions.mapDataSearchExport) &&
                <DataExport data={items} selected={selectedOption} mapview={mapview} resultCount={resultData.features.length} activeSearch={activeSearch} />
            }

            {uniqueLayerNames && uniqueLayerNames?.length > 0 && (
                <div className="form-group">
                    <label className="mb-1">{t('layer')}</label>
                    {/* <select
                        id="irlayer-select"
                        className="form-select"
                        value={selectedOption}
                        onChange={handleIResultLayerChange}
                    >
                        {uniqueLayerNames.map((lname, resultIndex) => (
                            <option
                                key={`option-${lname}-${resultIndex}`}
                                value={lname}
                                className="irlayer-option"
                            >
                                {lname}
                            </option>
                        ))}
                    </select> */}
                    <Select
                        id="layer-select"
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        className={'react-select-container'}
                        classNamePrefix={'react-select'}
                        options={layerNames}
                        value={layerNames.find((option) => option.value === selectedOption)}
                        onChange={handleIResultLayerChange}
                        isSearchable={true}
                    />
                </div>
            )}
            {resultData && (
                resultData?.features?.length > 0 ? (
                    <Fragment>
                        <div className="iactions mb-1 p-1 w-100 d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <input className="form-check-input focusBorderNone" type="checkbox" id="viewonlyFeatures" checked={viewonlyFeatures} onChange={handleViewOnlyFeatures} />
                                <label htmlFor="viewonlyFeatures" className="ms-2 me-2 mb-0 form-check-label">{t('viewonlyFeatures')}</label>
                            </div>
                            <div className="selectAll d-flex">
                                <input
                                    type="checkbox"
                                    checked={selectAllChecked}
                                    onChange={toggleSelectAll}
                                    className="form-check-input focusBorderNone"
                                />
                                <label className="label form-check-label ps-2 mb-0">{t('selectAll')}</label>

                            </div>
                            <div className="highlight"></div>
                            <div className={`${selectedFeatures.length > 0 ? 'active' : 'disabled'} zoom`} title={t('zoom')}
                                onClick={zoomToSelected}
                            >
                                <FiZoomIn size={16} />
                            </div>
                        </div>
                        <div className="items">
                            {resultData.features.map((result, index) => {
                                const featureID = getValueCaseInsensitive(result.attributes, 'objectid');
                                const filteredAttributes = getFilteredAttributes(selectedOption, result.attributes);
                                return (
                                    <div className={`attribute-table w-100 ${selectedIndices.includes(index) ? 'active' : ''}`}
                                        key={`${result.layerName}-${index}`}
                                        onClick={(evt) => handleIResultItemClick(result, index)}
                                    >
                                        <div className="desc-title d-flex justify-content-between">
                                            <div className="label">
                                                <div className={`${showDetailsFor === featureID ? 'expanded' : 'expand'}`}
                                                    onClick={(evt) => toggleItemDetails(evt, featureID)}>
                                                    {showDetailsFor === featureID ? <FaMinus size={16} /> : <FaPlus size={16} />}
                                                </div>


                                                {result.attributes[resultData.displayFieldName]
                                                    ? result.attributes[resultData.displayFieldName]
                                                    : (() => {
                                                        // Find the first string property in result.attributes
                                                        for (const key in result.attributes) {
                                                            const attrValue = result.attributes[key];
                                                            if (typeof attrValue === 'string' && attrValue !== null && attrValue.trim() !== '') {
                                                                return attrValue;
                                                            } else if (typeof attrValue === 'number' && attrValue !== null && key === 'OBJECTID') {
                                                                return attrValue;
                                                            }
                                                        }
                                                        // Fallback if no string properties are found
                                                        return 'No Data';
                                                    })()
                                                }
                                            </div>
                                        </div>
                                        <div className={`${showDetailsFor === featureID ? 'desc-items' : 'hide'}`}>
                                            {Object.entries(filteredAttributes).map(([key, value], index) => {
                                                if (typeof value === 'number' && !Number.isInteger(value)) {
                                                    value = value.toFixed(2)
                                                }
                                               //  Check if it's a valid timestamp (e.g., 13-digit number)
                                               else if (
                                                    typeof value === "number" &&
                                                    Math.abs(value).toString().length >= 12 && // Likely milliseconds timestamp
                                                    Math.abs(value) > 100000000000 // Ensure not a small number like OBJECTID
                                                ) {
                                                    const date = new Date(value);
                                                    value = date.toLocaleDateString();
                                                }
                                                else {
                                                    value = value
                                                }

                                                return (
                                                    <div className='desc-item' key={`desc-item-${index}`}>
                                                        <div className='cell label'>{key}</div>
                                                        <div className='cell content'>{value}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <label className="label form-check-label ps-2 mt-2">Search Results : {searchResultsCount}</label>
                    </Fragment>
                ) : (
                    <div className="attribute-table w-100 m-1 no-data text-danger p-2">
                        {t('noResultsFound')}
                    </div>
                )
            )}
        </div>
    );
});

export default SearchResult;
